import { v4 as uuidv4 } from "uuid";
import type { CreateTemplateTilePattern1Draft } from "../Redux/UserSlice/userSlice";
import { defaultCreateTemplateTilePattern1 } from "../Redux/UserSlice/userSlice";

/** Same nesting as GET `GetTemplateData`: inner `data` if it holds `watermarks`, else outer. */
// Purpose: Check the watermarks location, it may be response.data.watermarks or response.data.data.watermarks. So, inner.data is used.
export function getTemplateMetadataRootFromGetResponse(response: {
  data?: unknown;
}): Record<string, unknown> {
  const data = response?.data as Record<string, unknown> | undefined;
  if (!data) return {};
  const inner = data.data as Record<string, unknown> | undefined;
  if (inner && Array.isArray(inner.watermarks)) {
    return inner;
  }
  return data;
}

export function deNormalizeAngle(angle: number): number {
  if (angle > 360 || angle < 0) return 0;
  return (angle > 180) ? angle - 360 : angle;
}

/**
 * Hydrate Redux tile draft when loading a template (API or cached template batch).
 */
export function tileDraftFromTemplateApiRoot(
  root: Record<string, unknown> | null | undefined
): CreateTemplateTilePattern1Draft {
  const r = root && typeof root === "object" ? root : {};
  if (r.patternType !== "tile") {
    return { ...defaultCreateTemplateTilePattern1 };
  }
  const td = r.tileData;
  if (!td || typeof td !== "object" || Array.isArray(td)) {
    return { ...defaultCreateTemplateTilePattern1 };
  }
  const t = td as Record<string, unknown>;
  const num = (v: unknown, d: number) => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : d;
  };
  // Convert t.type (Tile, Cross, Plus, Grid Cross) into lowercase
  // P-1 Tile, P-2 Cross, P-3 Plus and P-4 Grid Cross
  const typeStr = String(t.type ?? "").toLowerCase();
  const localId = String(t.localId ?? "");
  const isPlus =
    typeStr === "plus" || localId === "tile_pattern_3";
  const isCross =
    typeStr === "cross" || localId === "tile_pattern_2";
  const isGridCross =
    typeStr === "grid cross" ||
    localId === "tile_pattern_4";
  return {
    tiledEnabled: true,
    tilePatternId: isPlus
      ? "pattern3"
      : isCross
        ? "pattern2"
        : isGridCross
          ? "pattern4"
          : "pattern1",
    size: num(t.size, defaultCreateTemplateTilePattern1.size),
    spacing: num(t.spacing, defaultCreateTemplateTilePattern1.spacing),
    opacity: Math.min(1, Math.max(0, num(t.opacity, 1))),
    rotation: deNormalizeAngle(num(t.rotation, 0) as number),
  };
}

export function normalizeAngle(angle: number): number {
  if (angle > 180 || angle < -180) return 0;
  return (angle < 0) ? angle + 360 : angle;
}

/** Merges into template root when saving a new template with a tiled pattern (1–4) enabled. */
export function buildCreateTemplateTileRootFields(
  draft: CreateTemplateTilePattern1Draft
): Record<string, unknown> {
  if (!draft.tiledEnabled || !draft.tilePatternId || draft.tilePatternId === "none") {
    return {};
  }
  if (draft.tilePatternId === "pattern1") {
    return {
      patternType: "tile",
      tileData: {
        id: uuidv4(),
        localId: "tile_pattern_1",
        type: "Tile",
        size: draft.size,
        spacing: draft.spacing,
        rotation: normalizeAngle(draft.rotation),
        opacity: draft.opacity,
        name: "Tile",
      },
    };
  }
  if (draft.tilePatternId === "pattern2") {
    return {
      patternType: "tile",
      tileData: {
        id: uuidv4(),
        localId: "tile_pattern_2",
        type: "Cross",
        size: draft.size,
        spacing: draft.spacing,
        rotation: normalizeAngle(draft.rotation),
        opacity: draft.opacity,
        name: "Cross",
      },
    };
  }
  if (draft.tilePatternId === "pattern3") {
    return {
      patternType: "tile",
      tileData: {
        id: uuidv4(),
        localId: "tile_pattern_3",
        type: "Plus",
        size: draft.size,
        spacing: draft.spacing,
        rotation: normalizeAngle(draft.rotation),
        opacity: draft.opacity,
        name: "Plus",
      },
    };
  }
  if (draft.tilePatternId === "pattern4") {
    return {
      patternType: "tile",
      tileData: {
        id: uuidv4(),
        localId: "tile_pattern_4",
        type: "Grid Cross",
        size: draft.size,
        spacing: draft.spacing,
        rotation: normalizeAngle(draft.rotation),
        opacity: draft.opacity,
        name: "Grid cross",
      },
    };
  }
  return {};
}
